import { useCallback, useRef, useEffect } from "react";
import axios from "axios";
import dotenv from "react-dotenv";
import { useAuth } from "./useAuth";

/**
 * Centralized API hook with automatic authentication. Default headers are JSON with Bearer token.
 *
 * Usage:
 *   const { apiCall, get, post, put, del, cancelRequest } = useApi();
 *
 *   apiCall({ url: "/institutions/v1/patients", params: { page: 1 }, onSuccess: (res) => setData(res.data) });
 *   post("/institutions/v1/patients", { name: "John" }, { onSuccess: (res) => console.log(res.data) });
 *
 * File upload:
 *   const formData = new FormData(); formData.append("file", file);
 *   post("/institutions/v1/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
 *
 * Chat API:
 *   apiCall({ url: "/chat/v1/chatify/api/getContacts", useChatToken: true, onSuccess: setContacts });
 *
 * Request cancellation (auto-cancels previous request when cancelable is true):
 *   get("/institutions/v1/patients", { cancelable: true, params: { page: 1 }, onSuccess: setData });
 *   get("/institutions/v1/patients", { cancelable: true, params: { page: 2 }, onSuccess: setData }); // Cancels previous
 *
 * Manual cancellation:
 *   const { get, cancelRequest } = useApi();
 *   get("/institutions/v1/patients", { onSuccess: setData });
 *   cancelRequest(); // Manually cancel the request
 */
export const useApi = () => {
  const { user } = useAuth();
  const abortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const apiCall = useCallback(
    ({
      url,
      method = "GET",
      data = null,
      params = null,
      headers = null,
      onSuccess = null,
      onError = null,
      onFinally = null,
      useChatToken = false,
      timeout = 30000,
      cancelable = false,
      responseType = null,
    }) => {
      const fullUrl = url.startsWith("http") ? url : new URL(url, dotenv.API_URL).href;
      const token = useChatToken ? user?.user?.chat_user_token : user?.token;

      const defaultHeaders = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: token ? `Bearer ${token}` : undefined,
      };

      const finalHeaders = headers ? { ...defaultHeaders, ...headers } : defaultHeaders;
      if (!finalHeaders.Authorization) delete finalHeaders.Authorization;

      const axiosConfig = {
        url: fullUrl,
        method: method.toUpperCase(),
        headers: finalHeaders,
        timeout,
        ...(responseType && { responseType }),
      };

      // Handle request cancellation
      if (cancelable) {
        // Cancel previous request if exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();
        axiosConfig.signal = abortControllerRef.current.signal;
      }

      if (data && ["POST", "PUT", "PATCH"].includes(method.toUpperCase())) {
        axiosConfig.data = data;
      }

      if (params) {
        axiosConfig.params = params;
      }

      return axios(axiosConfig)
        .then(async (response) => {
          if (onSuccess) await onSuccess(response);
          return response;
        })
        .catch(async (error) => {
          // Handle cancelled requests silently - they're expected behavior
          if (axios.isCancel(error) || error.name === "CanceledError") {
            // Cancellation is normal when requests are superseded - don't treat as error
            // Return a resolved promise with a cancellation marker to avoid uncaught rejection
            return { canceled: true };
          }

          // For actual errors, call the error handler and re-throw
          if (onError) await onError(error);
          throw error;
        })
        .finally(() => {
          if (onFinally) onFinally();
        });
    },
    [user]
  );

  const get = useCallback(
    (url, config = {}) => apiCall({ url, method: "GET", ...config }),
    [apiCall]
  );

  const post = useCallback(
    (url, data, config = {}) => apiCall({ url, method: "POST", data, ...config }),
    [apiCall]
  );

  const put = useCallback(
    (url, data, config = {}) => apiCall({ url, method: "PUT", data, ...config }),
    [apiCall]
  );

  const patch = useCallback(
    (url, data, config = {}) => apiCall({ url, method: "PATCH", data, ...config }),
    [apiCall]
  );

  const del = useCallback(
    (url, config = {}) => apiCall({ url, method: "DELETE", ...config }),
    [apiCall]
  );

  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return { apiCall, get, post, put, patch, del, cancelRequest };
};

export default useApi;
